import React, { useCallback, useEffect, useRef, useState } from "react";

import { Tooltip } from "@mui/material";
import ShipeeziNewGrid, {
  Column,
  Icons as GridIcons,
  RowActionsExtra,
} from "Components/AppGrid/shipeezi-new-grid";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";

import { Color, Colors } from "../../../../../../../components/Form/Button";
import ConfirmChangeStatusModal from "../../../../../../../components/Generic/ConfirmChangeStatusModal";
import { PropHandles } from "../../../../../../../components/Generic/ViewJobComponent";
import { useStorageBoxTypesHook } from "../../../../../../../hooks/useStorageBoxTypesActions";
import { Row } from "../../../../../../../models/shipeezi.table";
import { StorageBoxType } from "../../../../../../../models/storage.box.type";
import { convertBasicUnits, handleGetConvertValue } from "../../../../../../../util/converts";
import { Status } from "../../../../../../../util/enums";
import { getMeasurement } from "../../../../../../../util/getMeasurement";
import { saveRowsSelectedFromTable } from "../../../../../../../util/selectRowsFromTable";
import { EditIcon } from "../../../../../Moving/Warehousing/Transshipments/styles";
import ConfirmDeleteStorageBoxTypeModal, {
  DeleteStorageBoxTypeHandle,
} from "./ConfirmDeleteStorageBoxTypeModal";
import { StorageBoxTypesPageModal } from "./StorageBoxTypesPageModal";
import { StorageBoxTypeViewModal } from "./StorageBoxTypeViewModal";
import { BadgeStatus, ButtonChangeStatus, ChangeStatusIcon, DeleteIcon, ViewIcon } from "./styles";
import { useAppState } from "Context/StateContext";
import { isShowAction } from "Util/permissions";
import { formatDateWithUserPref } from "Util/dateFormaterPreference";

type Props = {
  permission?: {
    edit: boolean;
    create: boolean;
  };
};

export const StorageBoxTypesPage = ({ permission }: Props) => {
  const {
    page,
    total,
    pageSize,
    hasError,
    setPageSize,
    handleSearch,
    handleFilter,
    handleSorting,
    handleCurrentPage,
    handleGetDefaultFilters,
    storageBoxTypesData,
    getStorageBoxTypesList,
    getStorageBoxTypesCount,
    useCreateStorageBoxType,
    useUpdateStorageBoxType,
    jobTypes,
    isFetchingJobTypes,
  } = useStorageBoxTypesHook();

  const measurement = getMeasurement();
  const [openNewStorageBoxTypeModal, setOpenNewStorageBoxTypeModal] = useState<boolean>(false);
  const [selectedStorageBoxType, setSelectedStorageBoxType] = useState<StorageBoxType>();
  const [openConfirmation, setOpenConfirmation] = useState<boolean>(false);
  const [rows, setRows] = useState<Row[]>([]);
  const [clearSelection, setClearSelection] = useState<boolean>(false);
  const { currentUser } = useAppState();

  const { t: translate } = useTranslation();
  const { convertMetersToMillimeters } = convertBasicUnits();
  const sideViewRef = useRef<PropHandles>(null);

  const deleteStorageBoxTypeModalRef = useRef<DeleteStorageBoxTypeHandle>(null);

  const { isFetching } = getStorageBoxTypesList();

  getStorageBoxTypesCount();

  const { mutate: updateStorageBoxType, isLoading: isUpdating } = useUpdateStorageBoxType();
  const { mutate: createStorageBoxType, isLoading: isCreating } = useCreateStorageBoxType();

  useEffect(() => {
    if (!hasError) {
      setClearSelection(false);
      setRows([]);
    }
  }, [hasError]);

  const onOpenConfirmationModal = (storageBoxType?: StorageBoxType) => {
    if (storageBoxType) deleteStorageBoxTypeModalRef?.current?.onOpenModal(storageBoxType);
    else deleteStorageBoxTypeModalRef?.current?.onCloseModal();
  };

  const onDelete = (storageBoxTypeToDelete?: StorageBoxType) => {
    if (storageBoxTypeToDelete) {
      updateStorageBoxType({
        id: Number(storageBoxTypeToDelete.id),
        storageBoxType: {
          ...storageBoxTypeToDelete,
          valid: false,
        },
      });
    }
  };

  const renderUsageColumnValue = (row?: StorageBoxType) => {
    return (
      <span>
        {`${(!!row?.isStorageBoxUsageRoad && translate("road")) || ""}
          ${(!!row?.isStorageBoxUsageRoad && !!row?.isStorageBoxUsageWarehouse && "/") || ""}
          ${(!!row?.isStorageBoxUsageWarehouse && translate("warehouse")) || ""}`}
      </span>
    );
  };

  const columns: Column[] = [
    {
      name: "name",
      title: translate("name"),
    },
    {
      name: "createdAt",
      title: translate("dateCreated"),
      hideSearch: true,
      formatComponent: (createdAt?: Date) =>
        createdAt &&
        currentUser?.preferences && (
          <span>{formatDateWithUserPref(createdAt, currentUser.preferences, "dateOnly")}</span>
        ),
    },
    {
      name: "height",
      title: translate("height"),
      formatText: (height: number) => {
        return `${convertMetersToMillimeters(height)} mm`;
      },
      hideSearch: true,
    },
    {
      name: "width",
      title: translate("width"),
      formatText: (width: number) => {
        return `${convertMetersToMillimeters(width)} mm`;
      },
      hideSearch: true,
    },
    {
      name: "depth",
      title: translate("depth"),
      formatText: (depth: number) => {
        return `${convertMetersToMillimeters(depth)} mm`;
      },
      hideSearch: true,
    },
    {
      name: "capacity",
      title: translate("capacity"),
      formatText: (capacity: number) => {
        return `${handleGetConvertValue(
          capacity || 0,
          measurement?.code,
          measurement?.decimalPlaces
        )} ${measurement?.code}`;
      },
      hideSearch: true,
    },
    {
      name: "usage",
      title: translate("usage"),
      formatComponent: (_, row?: StorageBoxType) => renderUsageColumnValue(row),
    },
    {
      name: "isActive",
      title: translate("status"),
      hideSearch: true,
      formatComponent: (status: boolean) => (
        <BadgeStatus isActive={status}>{status ? Status.ACTIVE : Status.INACTIVE}</BadgeStatus>
      ),
    },
  ];

  const editStorageBoxType = (storageBoxTypeId: number) => {
    const storageBoxTypeToUpdate = storageBoxTypesData.find(
      (storageBoxType) => Number(storageBoxType.id) === Number(storageBoxTypeId)
    );

    if (storageBoxTypeToUpdate) {
      setSelectedStorageBoxType(storageBoxTypeToUpdate);
      setOpenNewStorageBoxTypeModal(true);
    }
  };

  const viewStorageBoxType = (index: number) => {
    const storageBoxTypeToView = storageBoxTypesData.find(
      (storageBoxType) => Number(storageBoxType.id) === Number(index)
    );

    return (
      <Tooltip title={translate("view")} placement="top">
        <div>
          <ViewIcon
            size={18}
            color="#475467"
            onClick={() => {
              setSelectedStorageBoxType(storageBoxTypeToView);
              sideViewRef.current?.setOpenSideBar(true);
            }}
          />
        </div>
      </Tooltip>
    );
  };

  const handleEditButton = (id: string | number, storageBoxType: StorageBoxType) => {
    if (storageBoxType?.systemDefault) return null;

    return (
      <Tooltip data-cy="action-edit" title={translate("edit")} placement="top">
        <div>
          <EditIcon onClick={() => editStorageBoxType(Number(id))} color="#475467" size={18} />
        </div>
      </Tooltip>
    );
  };

  const changeStatusStorageBoxType = (index: number) => {
    const storageBoxTypeToUpdate = storageBoxTypesData.find(
      (storageBoxType) => Number(storageBoxType.id) === Number(index)
    );

    const doesTypeHaveAnActiveStorageBox = storageBoxTypeToUpdate?.storageBoxes?.some(
      (storageBox) => !!storageBox.isActive && storageBox.valid && !storageBox.singleUseJobId
    );

    return (
      <Tooltip
        title={
          doesTypeHaveAnActiveStorageBox
            ? translate("thisStorageBoxTypeIsCurrentlyInUseAndItsStatusCantBeChanged")
            : translate("changeStatus")
        }
        placement="top"
      >
        <div>
          <ChangeStatusIcon
            isActive={!doesTypeHaveAnActiveStorageBox}
            data-cy="action-change"
            color={doesTypeHaveAnActiveStorageBox ? "#98A2B3" : "#475467"}
            onClick={() => {
              if (storageBoxTypeToUpdate && !doesTypeHaveAnActiveStorageBox) {
                updateStorageBoxType({
                  id: Number(storageBoxTypeToUpdate.id),
                  storageBoxType: {
                    ...storageBoxTypeToUpdate,
                    isActive: !storageBoxTypeToUpdate.isActive,
                  },
                });
              }
            }}
          />
        </div>
      </Tooltip>
    );
  };

  const deleteStorageBoxType = (index: number) => {
    const storageBoxTypeToDelete = storageBoxTypesData.find(
      (storageBoxType) => Number(storageBoxType.id) === Number(index)
    );

    if (
      storageBoxTypeToDelete?.storageBoxes &&
      storageBoxTypeToDelete?.storageBoxes?.length === 0 &&
      !storageBoxTypeToDelete?.systemDefault
    ) {
      return (
        <Tooltip title={translate("delete")} placement="top">
          <div>
            <DeleteIcon
              size={18}
              color="#F04438"
              onClick={() => {
                if (storageBoxTypeToDelete) {
                  onOpenConfirmationModal(storageBoxTypeToDelete);
                }
              }}
            />
          </div>
        </Tooltip>
      );
    }

    return null;
  };

  const renderButtonToChangeStatus = useCallback(() => {
    if (rows.length > 0) {
      return (
        <ButtonChangeStatus
          tooltip={translate("changeStatus")}
          customColor={Color.INFO}
          data-cy="changeStatus"
          onClick={() => setOpenConfirmation(true)}
        >
          <GridIcons.status color={Colors.INFO.text.color} size={16} />
          {translate("changeStatus")}
        </ButtonChangeStatus>
      );
    }
  }, [rows]);

  const handleSaveStorageBoxTypeSelected = (rowsArr: number[]) => {
    const storageBoxTypes = storageBoxTypesData?.map((storageBoxType) => ({
      ...storageBoxType,
      status: storageBoxType?.isActive ? Status.ACTIVE : Status.INACTIVE,
    }));
    const rowsSelected: Row[] = saveRowsSelectedFromTable(rowsArr, rows, storageBoxTypes);

    if (rowsArr.length === 0) setRows([]);
    else setRows([...rowsSelected]);
  };

  const handleRowActionsExtraByPermission = () => {
    let actions: RowActionsExtra[] = [];

    if (permission && isShowAction(currentUser, permission?.edit)) {
      actions?.push({
        title: translate("edit"),
        key: "edit",
        buttonAndAction: handleEditButton,
      });
    }

    actions?.push(
      { title: translate("view"), key: "handleView", buttonAndAction: viewStorageBoxType },
      {
        title: translate("changeStatus"),
        key: "handleChangeStatus",
        buttonAndAction: changeStatusStorageBoxType,
      },
      {
        title: translate("delete"),
        key: "handleDeleteStorageBoxType",
        buttonAndAction: deleteStorageBoxType,
      }
    );

    return actions;
  };

  return (
    <div data-cy="storageBoxesGrid">
      <ShipeeziNewGrid
        data={storageBoxTypesData}
        columns={columns}
        totalItems={total}
        loadingData={isFetching || isUpdating || isCreating}
        emptyData={translate("noInformationToBeShown")}
        pageSize={pageSize}
        pageSizeList={[15, 40, 60, 100]}
        onChangePageSize={setPageSize}
        currentPage={page}
        showSelection={true}
        onNextPage={handleCurrentPage}
        onPreviousPage={handleCurrentPage}
        onGoToPage={handleCurrentPage}
        onSorting={handleSorting}
        handleSelect={(rows: number[]) => handleSaveStorageBoxTypeSelected(rows)}
        clearSelection={clearSelection}
        generalActions={{
          orderBy: {
            right: ["handleChangeStatus", "handleNewRecord"],
            left: ["handleSearch", "handleFilterBy"],
          },
          handleSearch: {
            onSearch: (filters) => handleSearch(filters),
          },
          handleFilterBy: {
            categories: [
              {
                name: translate("status"),
                column: "isActive",
                optionsSelected: handleGetDefaultFilters(),
                options: [
                  { value: "1", label: translate("active") },
                  { value: "0", label: translate("inactive") },
                ],
              },
            ],
            onChange: handleFilter,
          },
          handleNewRecord:
            permission && isShowAction(currentUser, permission?.create)
              ? {
                  text: translate("new"),
                  action: () => {
                    setSelectedStorageBoxType(undefined);
                    setOpenNewStorageBoxTypeModal(true);
                  },
                }
              : undefined,
          handleChangeStatus: renderButtonToChangeStatus(),
        }}
        rowActions={{
          orderBy: ["edit", "handleView", "handleChangeStatus", "handleDeleteStorageBoxType"],
        }}
        rowActionsExtra={handleRowActionsExtraByPermission()}
        positionActionsColumn="right"
      />
      {openNewStorageBoxTypeModal && (
        <StorageBoxTypesPageModal
          measurement={measurement}
          isFieldsDisabled={isUpdating || isCreating}
          open={openNewStorageBoxTypeModal}
          hasError={hasError}
          closeModal={() => setOpenNewStorageBoxTypeModal(false)}
          storageBoxTypeToUpdate={selectedStorageBoxType}
          createStorageBoxType={(storageBoxType: StorageBoxType) =>
            createStorageBoxType(storageBoxType)
          }
          updateStorageBoxType={(storageBoxType: StorageBoxType, storageBoxTypeId: number) => {
            updateStorageBoxType({ storageBoxType, id: storageBoxTypeId });
          }}
          isLoading={isUpdating || isCreating}
          jobTypes={jobTypes}
          isLoadingJobTypes={isFetchingJobTypes}
        />
      )}
      {openConfirmation && (
        <ConfirmChangeStatusModal
          open={openConfirmation}
          rows={rows}
          isLoading={isUpdating}
          hasErrors={hasError}
          closeModal={() => setOpenConfirmation(false)}
          update={(rowId, status) => {
            if (rowId) {
              const storageBoxType = storageBoxTypesData?.find(
                (storageBoxType) => storageBoxType.id === rowId
              );

              if (storageBoxType) {
                updateStorageBoxType({
                  id: rowId,
                  storageBoxType: {
                    ...storageBoxType,
                    isActive: status === Status.ACTIVE,
                  } as StorageBoxType,
                });
              }
              setClearSelection(true);
            }
          }}
        />
      )}
      <StorageBoxTypeViewModal
        storageBoxType={selectedStorageBoxType}
        loading={isFetching}
        sideViewRef={sideViewRef}
      />
      <ConfirmDeleteStorageBoxTypeModal
        ref={deleteStorageBoxTypeModalRef}
        onDelete={onDelete}
        isDeleting={isUpdating}
        hasError={hasError}
      />
    </div>
  );
};
